import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FiHelpCircle } from "react-icons/fi";
import { PiBookOpenText } from "react-icons/pi";

// Components
import Button from "../../button";

// Styles
import styles from "./Content.module.scss";

// Types
import { Card } from "../../../types";

// Hooks
import useReadAloudButton from "../../../hooks/useReadAloudButton";
import LightBulb from "../../Svgs/LightBulb";

interface ContentProps {
  card: Card;
  isFlipped: boolean;
  showHint: boolean;
  setShowHint: (showHint: boolean) => void;
  handleFlip: () => void;
  skippingHints?: boolean;
  children: React.ReactNode;
}

const Content: React.FC<ContentProps> = ({ card, showHint, setShowHint, isFlipped, children, handleFlip, skippingHints }) => {
  const { t } = useTranslation();

  const { ReadAloudButton, Text } = useReadAloudButton(
    (isFlipped ? card?.definition : card?.term) ?? ""
  );

  useEffect(() => {
    if (showHint) {
      setShowHint(false);
    }
  }, [card, card?.id]);

  const handleLearnMore = useCallback(() => {
    // TODO: Implement learn more functionality
  }, []);

  const handleGetHint = useCallback(() => {
    setShowHint(!showHint);
  }, [showHint]);

  const handleGetHintClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    handleGetHint();
  }, [handleGetHint]);

  if (!card) {
    return (
      <div className={styles.container}>
        <div className={styles.noCard}>
          <p>{t('flashcardReview.noCard')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.container} bg-white dark:bg-stone-650`}>
      <div className={styles.learningSection}>
        <div className={styles.learningHeader}>
          <h2 className={`${styles.learningTitle} dark:text-white`}>
            {t('flashcardReview.learnConcept')}
          </h2>
          <p className={`${styles.learningSubtitle} dark:text-white`}>
            {t('flashcardReview.justReadAndUnderstand')}
          </p>
        </div>

        <ReadAloudButton className={`${styles.readAloudButton} dark:text-white`} />
      </div>

      <div
        className={`${styles.cardContent} ${isFlipped ? styles.flipped : ''}`}
        onClick={handleFlip}
      >
        <div className={`${styles.cardFront} bg-white dark:bg-stone-650`}>
          <h3 className={`${styles.cardTerm} dark:text-white`} dir="auto">{card.term}</h3>
          {/* <div className={styles.learnMoreLink}>
            <PiBookOpenText size={24} />
            <span onClick={(e) => { e.stopPropagation(); handleLearnMore(); }}>
              {t('flashcardReview.learnMore')}
            </span>
          </div> */}
          {!skippingHints && (
            <div className={styles.hintSection}>
              <button
                type="button"
                onClick={handleGetHintClick}
                className={`${styles.hintButton} ${styles.button}`}
              >
                <FiHelpCircle />
                {showHint ? t('flashcardReview.hideHint') : t('flashcardReview.getHint')}
              </button>

            </div>
          )}
          {showHint && !skippingHints && (
              <div className={`${styles.cardHint} dark:text-white`}>
                <span className={`${styles.hintLabel} dark:text-white`}>
                  {t('flashcardReview.hint')}:
                </span>
                <span className={`${styles.hintText} dark:text-white`} dir="auto">{card.hint}</span>
              </div>
            )}
        </div>

        <div className={styles.cardBack}>
          <p className={styles.cardDefinition} dir="auto">
            <Text />
          </p>
        </div>
      </div>
      {children}
    </div>
  );
};

export default Content;
